import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class PhotoFilterService {
  private readonly NOVEMBER_FIRST_2025 = new Date("2025-11-01");

  /**
   * Filters photos based on the new photo display logic:
   * - For skid steer or mini excavator: use ALL catalog photos
   * - For other machines: exclude photos with "ai-generated" (but NOT "ai-generated-2") that are newer than November 1st
   * @param photos Array of photo paths
   * @param primaryType Machine type (e.g., "Skid Steer Loader", "Mini Excavator")
   * @returns Filtered array of photo paths
   */
  filterPhotos(photos: string[], primaryType: string): string[] {
    if (!photos || photos.length === 0) {
      return photos;
    }

    const normalizedType = primaryType?.toLowerCase() || "";
    const isSkidSteerOrMiniEx = normalizedType.includes("skid steer") || normalizedType.includes("mini excavator") || normalizedType.includes("mini ex");

    // For skid steer or mini excavator, return all photos
    if (isSkidSteerOrMiniEx) {
      return photos;
    }

    // For other machines, filter out bad AI-generated photos
    return photos.filter(photo => {
      // Extract date from filepath (e.g., "/pubweb/catalog/.../20251113/...")
      const dateMatch = photo.match(/\/(\d{8})\//);

      if (!dateMatch) {
        // If no date in path, keep the photo
        return true;
      }

      const dateStr = dateMatch[1]; // e.g., "20251113"
      const year = parseInt(dateStr.substring(0, 4));
      const month = parseInt(dateStr.substring(4, 6));
      const day = parseInt(dateStr.substring(6, 8));
      const photoDate = new Date(year, month - 1, day);

      // If photo is older than November 1st, exclude it
      if (photoDate < this.NOVEMBER_FIRST_2025) {
        return false;
      }

      // Photo is newer than November 1st - now check the path
      const hasAiGenerated = photo.includes("ai-generated");
      const hasAiGenerated2 = photo.includes("ai-generated-2");

      // If it has NO "ai-generated" in path at all, keep it
      if (!hasAiGenerated) {
        return true;
      }

      // If it has "ai-generated-2", keep it
      if (hasAiGenerated2) {
        return true;
      }

      // If we get here, it has "ai-generated" but NOT "ai-generated-2" - exclude it
      return false;
    });
  }
}
